import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import Layout from "@/components/site/Layout";
import ProtectedRoute from "@/components/site/ProtectedRoute";
import CustomerProtectedRoute from "@/components/customer/CustomerProtectedRoute";
import CookieBanner from "@/components/site/CookieBanner";
import { initAnalytics } from "@/lib/analytics";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AccountDeletion from "@/pages/AccountDeletion";
import NotFound from "@/pages/NotFound";
import Rates from "@/pages/Rates";
import FAQ from "@/pages/FAQ";
import HowItWorks from "@/pages/HowItWorks";
import CookiePolicy from "@/pages/CookiePolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import PublicDialer from "@/pages/PublicDialer";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminV1 from "@/pages/admin/AdminV1";

import Register from "@/pages/customer/Register";
import Login from "@/pages/customer/Login";
import VerifyEmail from "@/pages/customer/VerifyEmail";
import ForgotPassword from "@/pages/customer/ForgotPassword";
import ResetPassword from "@/pages/customer/ResetPassword";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import CustomerWallet from "@/pages/customer/CustomerWallet";
import CustomerTopUp from "@/pages/customer/CustomerTopUp";
import CustomerTopUpSuccess from "@/pages/customer/CustomerTopUpSuccess";
import CustomerEarn from "@/pages/customer/CustomerEarn";
import { CustomerSupportList, CustomerSupportThread } from "@/pages/customer/CustomerSupport";
import CustomerDialer from "@/pages/customer/CustomerDialer";
import CustomerCallHistory from "@/pages/customer/CustomerCallHistory";
import CustomerContacts from "@/pages/customer/CustomerContacts";
import CustomerMessages, { CustomerMessageThread, CustomerMessageNew } from "@/pages/customer/CustomerMessages";

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <CustomerAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/dialer" element={<PublicDialer />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/rates" element={<Rates />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/privacy-policy" element={<Privacy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/terms-and-conditions" element={<Terms />} />
                <Route path="/account-deletion" element={<AccountDeletion />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Customer auth (no public site chrome) */}
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/app" element={
                <CustomerProtectedRoute><CustomerDashboard /></CustomerProtectedRoute>
              } />
              <Route path="/app/profile" element={
                <CustomerProtectedRoute><CustomerProfile /></CustomerProtectedRoute>
              } />
              <Route path="/app/wallet" element={
                <CustomerProtectedRoute><CustomerWallet /></CustomerProtectedRoute>
              } />
              <Route path="/app/wallet/topup" element={
                <CustomerProtectedRoute><CustomerTopUp /></CustomerProtectedRoute>
              } />
              <Route path="/app/wallet/topup/success" element={
                <CustomerProtectedRoute><CustomerTopUpSuccess /></CustomerProtectedRoute>
              } />
              <Route path="/app/earn" element={
                <CustomerProtectedRoute><CustomerEarn /></CustomerProtectedRoute>
              } />
              <Route path="/app/support" element={
                <CustomerProtectedRoute><CustomerSupportList /></CustomerProtectedRoute>
              } />
              <Route path="/app/support/:ticketId" element={
                <CustomerProtectedRoute><CustomerSupportThread /></CustomerProtectedRoute>
              } />
              <Route path="/app/dial" element={
                <CustomerProtectedRoute><CustomerDialer /></CustomerProtectedRoute>
              } />
              <Route path="/app/calls" element={
                <CustomerProtectedRoute><CustomerCallHistory /></CustomerProtectedRoute>
              } />
              <Route path="/app/contacts" element={
                <CustomerProtectedRoute><CustomerContacts /></CustomerProtectedRoute>
              } />
              <Route path="/app/messages" element={
                <CustomerProtectedRoute><CustomerMessages /></CustomerProtectedRoute>
              } />
              <Route path="/app/messages/new" element={
                <CustomerProtectedRoute><CustomerMessageNew /></CustomerProtectedRoute>
              } />
              <Route path="/app/messages/:phone" element={
                <CustomerProtectedRoute><CustomerMessageThread /></CustomerProtectedRoute>
              } />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
              } />
              <Route path="/admin/v1" element={
                <ProtectedRoute role="admin"><AdminV1 /></ProtectedRoute>
              } />
            </Routes>
            <CookieBanner />
          </BrowserRouter>
        </CustomerAuthProvider>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
